import { API_BASE_URL } from "../../config";
import { getAuthHeaders } from "../Auth/authService";
import { hostStore } from "./hostStore";
import { Host } from "./types/host";

const mapHost = (host: Host & { lastSeen?: string | null; }): Host => ({
	id: host.id,
	name: host.name,
	lastSeen: host.lastSeen ? new Date(host.lastSeen) : undefined,
	currentActivity: host.currentActivity ?? null,
});

let listAbortController: AbortController | null = null;

const abortList = (): void =>
{
	if (listAbortController)
	{
		listAbortController.abort();
		listAbortController = null;
	}
};

const list = async (): Promise<Host[]> =>
{
	abortList();
	listAbortController = new AbortController();

	const response = await fetch(`${API_BASE_URL}/hosts`, {
		method: "GET",
		headers: getAuthHeaders(),
		signal: listAbortController.signal
	});

	if (!response.ok)
	{
		throw new Error("Failed to load the server hosts");
	}

	const result = await response.json();
	const hosts = result.map(mapHost);

	listAbortController = null;
	hostStore.set(hosts);
	return hosts;
};

const verify = async (code: string): Promise<void> =>
{
	const response = await fetch(`${API_BASE_URL}/hosts/verify/${encodeURIComponent(code)}`, {
		method: "GET",
		headers: getAuthHeaders()
	});

	if (!response.ok)
	{
		const errorData = await response.json();
		throw new Error(errorData.error ?? "Failed to verify the registration code");
	}
};

const claim = async (code: string, name: string): Promise<Host> =>
{
	const response = await fetch(`${API_BASE_URL}/hosts/claim`, {
		method: "POST",
		headers: getAuthHeaders(),
		body: JSON.stringify({ code, name })
	});

	if (!response.ok)
	{
		const errorData = await response.json();
		throw new Error(errorData.error ?? "Failed to add the server host");
	}

	return response.json();
};

const updateName = async (id: string, name: string): Promise<void> =>
{
	const response = await fetch(`${API_BASE_URL}/hosts/${encodeURIComponent(id)}`, {
		method: "PUT",
		headers: getAuthHeaders(),
		body: JSON.stringify({ name })
	});

	if (!response.ok)
	{
		const errorData = await response.json();
		throw new Error(errorData.error ?? "Failed to update the server host name");
	}

	hostStore.updateName(id, name);
};

const remove = async (id: string): Promise<void> =>
{
	abortList();

	const response = await fetch(`${API_BASE_URL}/hosts/${encodeURIComponent(id)}`, {
		method: "DELETE",
		headers: getAuthHeaders()
	});

	if (!response.ok)
	{
		throw new Error("Failed to remove the server host");
	}

	hostStore.remove(id);
};

export const hostService = {
	list,
	verify,
	claim,
	updateName,
	remove,
};
